import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { StudentSection } from "./StudentLayout";
import { Calendar, Check } from "lucide-react";
import { todayISO } from "@/components/DateNav";

const TYPE_COLOR = { gi: "#3ddc7e", "no-gi": "#4f8eff", kids: "#f4b942", open: "#b266ff" };

export default function StudentClasses() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/student/classes/upcoming").then(r => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const reserve = async (k) => { await api.post("/student/reserve", { class_id: k.class_id, date: k.date }); load(); };
  const cancel = async (k) => { await api.post("/student/cancel", { class_id: k.class_id, date: k.date }); load(); };

  // Group by date
  const grouped = useMemo(() => {
    const g = {};
    for (const it of items) { (g[it.date] = g[it.date] || []).push(it); }
    return Object.entries(g);
  }, [items]);

  if (loading) return <div className="p-8 label">Loading…</div>;

  return (
    <div data-testid="page-student-classes">
      <StudentSection section="Next 7 days" title="Classes"/>
      <div className="px-5 space-y-5">
        {grouped.length === 0 && <div className="card p-6 text-center"><Calendar size={22} style={{color:"var(--text-muted)", margin:"0 auto"}}/><div className="label-tiny mt-3">No upcoming classes.</div></div>}
        {grouped.map(([date, classes]) => {
          const d = new Date(date + "T00:00:00");
          const anyToday = classes.some(c => c.is_today);
          const isToday = anyToday || date === todayISO();
          return (
            <div key={date}>
              <div className="flex items-baseline gap-3 mb-2 px-1">
                <span className="label" style={{color: isToday ? "var(--green)" : "var(--text-dim)"}}>
                  {isToday ? "TODAY" : d.toLocaleDateString("en-US", {weekday:"short"}).toUpperCase()}
                </span>
                <span className="label-tiny">{d.toLocaleDateString("en-US", {month:"short", day:"numeric"})}</span>
              </div>
              <div className="space-y-2">
                {classes.map(c => {
                  const full = c.reserved_count >= c.capacity && !c.reserved;
                  const color = TYPE_COLOR[c.type] || "#3ddc7e";
                  return (
                    <div key={`${c.class_id}-${c.date}`} className="card p-4 flex items-center gap-4" data-testid={`upc-${c.class_id}-${c.date}`}>
                      <div style={{ width: 4, alignSelf:"stretch", borderRadius: 2, background: color }} />
                      <div className="mono text-[13px]" style={{width:46, color:"var(--text-dim)"}}>{c.start_time}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold uppercase tracking-tight text-[14px] truncate">{c.name}</div>
                        <div className="label-tiny mt-0.5 truncate">{c.instructor} · {c.reserved_count}/{c.capacity}</div>
                      </div>
                      {c.reserved ? (
                        <button onClick={()=>cancel(c)} className="pill pill-active flex items-center gap-1" data-testid={`upc-cancel-${c.class_id}-${c.date}`}>
                          <Check size={11}/> Reserved
                        </button>
                      ) : full ? (
                        <span className="pill" style={{background:"transparent", color:"var(--text-muted)", border:"1px solid var(--border)"}}>Full</span>
                      ) : (
                        <button onClick={()=>reserve(c)} className="pill" style={{background:"transparent", color:"var(--green)", border:"1px solid var(--green-border)"}} data-testid={`upc-reserve-${c.class_id}-${c.date}`}>Reserve</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
